import { escapeHTML } from './utilities.js';

// Import utilities
const {
	isEmpty
} = _uo.utility;

/**
 * Parses markdown content
 */
export default class Markdown {
	/**
	 * Saves the content as a property
	 *
	 * @param {String} content The content
	 */
	constructor( content = '' ) {
		// Save content
		this.content = content;
	}

	/**
	 * Parses the markdown content and returns the HTML
	 *
	 * @return {String} The content parsed
	 */
	getHTML() {
		// Define content and escape HTML
		let content = escapeHTML( this.content );

		// Parse italic and bold
		content = this.parseItalicsAndBold( content );

		// Parse anchors
		content = this.parseAnchors( content );

		return content;
	}

	/**
	 * Parses bold and italic text on the text
	 * This method will convert:
	 * - **text** to <strong>text</strong>
	 * - __text__ to <strong>text</strong>
	 * - *text* to <em>text</em>
	 * - _text_ to <em>text</em>
	 *
	 * @param {String} content The content
	 * @return {String}         The content with the bolds and italics parsed
	 */
	parseItalicsAndBold( content = '' ) {
		const regularExpressions = {
			doubleUnderscores: /(^|\s|>|\b)__(?=\S)([^]+?)__(?=\b|<|\s|$)/gm,
			singleUnderscores: /(^|\s|>|\b)_(?=\S)([^]+?)_(?=\b|<|\s|$)/gm,
			doubleAsterisks: /(\*\*)(?=\S)([^\r]*?\S[*]*)\1/g,
			singleAsterisks: /(\*)(?=\S)([^\r]*?\S)\1/g,
		};

		// Underscores
		// Since we are consuming a \s character, we need to add it
		content = content.replace( regularExpressions.doubleUnderscores, '$1<strong>$2</strong>' );

		content = content.replace( regularExpressions.singleUnderscores, '$1<em>$2</em>' );

		// Asterisks
		content = content.replace( regularExpressions.doubleAsterisks, '<strong>$2</strong>' );

		content = content.replace( regularExpressions.singleAsterisks, '<em>$2</em>' );

		// Return content
		return content;
	}

	/**
	 * Parses anchors on the text
	 * This method will convert [link text](url "optional title") anchors to <a href="url" title="optional title">link text</a>
	 *
	 * @param {String} content The content
	 * @return {String}         The content with the anchors parsed
	 */
	parseAnchors( content = '' ) {
		// Create the regex
		// eslint-disable-next-line max-len
		const regExAnchors = /(\[((?:\[[^\]]*]|[^\[\]])*)]\([ \t]*()<?(.*?(?:\(.*?\).*?)?)>?[ \t]*((['"])(.*?)\6[ \t]*)?\))/g;

		// Create the replacer function
		/**
		 * Function to replace markdown anchor matches with HTML
		 *
		 * @param {String} wholeMatch The whole match
		 * @param {String} m1         The whole match
		 * @param {String} linkText   The link text
		 * @param {String} linkId     The link ID
		 * @param {String} url        The link URL
		 * @param {String} m5         The link title with quotes
		 * @param {String} m6         The link title' quotes
		 * @param {String} title      The link title
		 * @return {String}            The markdown anchor converted to HTML
		 */
		const replacerFunction = ( wholeMatch, m1, linkText, linkId, url, m5, m6, title = '' ) => {
			// Update the whole match
			wholeMatch = m1;

			// Convert the link ID to lowercase
			linkId = linkId.toLowerCase();

			// Check if the URL is defined
			if ( ! url ) {
				// Check if there is a link ID
				if ( ! linkId ) {
					// Lower-case and turn embedded newlines into spaces
					linkId = linkText.toLowerCase().replace( / ?\n/g, ' ' );
				}

				// Add the link ID as hash
				url = `#${ linkId }`;

				if ( wholeMatch.search( /\(\s*\)$/m ) > -1 ) {
					// Special case for explicit empty url
					url = '';
				} else {
					return wholeMatch;
				}
			}

			// Encode URL
			url = encodeURI( url, '*_', false );

			// Check if we have to add the title
			if ( ! isEmpty( title ) ) {
				// Encode content
				title = title.replace( /"/g, '&quot;' );
				title = encodeURI( title, '*_', false );

				// Add HTML format
				title = ` title="${ title }"`;
			} else {
				title = '';
			}

			const result = `
				<a href="${ url }" target="_blank" ${ title }>
					${ linkText }
				</a>
			`;

			return result;
		};

		// Inline-style links: [link text](url "optional title")
		content = content.replace( regExAnchors, replacerFunction );

		// Return content
		return content;
	}
}
